/**
 * Financial privacy context.
 *
 * When `hideFinancials` is true, all money figures across the app are
 * replaced with "••••". The toggle is surfaced on the dashboard (admin only).
 * State persists for the browser session (sessionStorage) — resets on tab close.
 */

import { createContext, useContext, useState, type ReactNode } from "react";

interface PrivacyContextValue {
  hideFinancials: boolean;
  toggleHideFinancials: () => void;
}

const PrivacyContext = createContext<PrivacyContextValue>({
  hideFinancials: false,
  toggleHideFinancials: () => {},
});

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [hideFinancials, setHideFinancials] = useState(
    () => sessionStorage.getItem("hideFinancials") === "true"
  );

  const toggleHideFinancials = () => {
    setHideFinancials(prev => {
      const next = !prev;
      sessionStorage.setItem("hideFinancials", String(next));
      return next;
    });
  };

  return (
    <PrivacyContext.Provider value={{ hideFinancials, toggleHideFinancials }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy(): PrivacyContextValue {
  return useContext(PrivacyContext);
}

/** Mask a formatted money string when privacy mode is on */
export function maskMoney(value: string, hide: boolean): string {
  return hide ? "••••" : value;
}
